import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../../core/common/Breadcrumb/breadcrumb";
import {
  FaSync,
  FaSearch,
  FaEye,
  FaHeart,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface BlogPost {
  id: number;
  title: string;
  publishDate: string;
  tags: string;
  authorName: string;
  imageUrl: string;
  excerpt: string;
  isHighlighted: boolean;
  viewCount: number;
  loveCount: number;
  language: string;
  languageDisplay: string;
  category: string;
  categoryDisplay: string;
}

const Blog = () => {
  const [allPosts, setAllPosts] = useState<BlogPost[]>([]);
  const [highlightedPosts, setHighlightedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResults, setSearchResults] = useState<BlogPost[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Category filter state (default is "ALL")
  const [activeCategory, setActiveCategory] = useState<string>("ALL");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(9); // 9 posts per page (3x3 grid)

  const categories = [
    { value: "ALL", label: "All Topics" },
    { value: "AUSBILDUNG", label: "Ausbildung" },
    { value: "GERMAN_LANGUAGE", label: "German Language" },
    { value: "STUDY", label: "Study" },
    { value: "FSJ", label: "FSJ" },
    { value: "AU_PAIR", label: "Au Pair" },
    { value: "SETTLEMENT", label: "Settlement" },
    { value: "CAREER", label: "Career" },
    { value: "LIFE_IN_GERMANY", label: "Life in Germany" },
  ];

  const fetchBlogs = async () => {
    setLoading(true);
    setError("");
    try {
      // Build URL with category parameter only
      let url = `${API_BASE}/api/public/blogs`;

      if (activeCategory !== "ALL") {
        url += `?category=${activeCategory}`;
      }

      const response = await fetch(url, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch blogs");

      const data: BlogPost[] = await response.json();

      // Separate highlighted and non-highlighted posts
      const highlighted = data.filter((post) => post.isHighlighted);
      const all = data.filter((post) => !post.isHighlighted);

      setHighlightedPosts(highlighted);
      setAllPosts(all);
      setCurrentPage(1); // Reset to first page when fetching new data
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchKeyword.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setCurrentPage(1); // Reset to first page when searching
    try {
      const response = await fetch(
        `${API_BASE}/api/public/blogs/search?keyword=${encodeURIComponent(
          searchKeyword
        )}`,
        { credentials: "include" }
      );

      if (!response.ok) throw new Error("Search failed");

      const results: BlogPost[] = await response.json();
      setSearchResults(results);
    } catch (err) {
      console.error("Search error:", err);
      setSearchResults([]);
    }
  };

  const clearSearch = () => {
    setSearchKeyword("");
    setSearchResults([]);
    setIsSearching(false);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchBlogs();
  }, [activeCategory]);

  // Pagination calculations
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = allPosts.slice(indexOfFirstPost, indexOfLastPost);
  const currentSearchResults = searchResults.slice(
    indexOfFirstPost,
    indexOfLastPost
  );
  const totalPages = Math.ceil(allPosts.length / postsPerPage);
  const totalSearchPages = Math.ceil(searchResults.length / postsPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatViewCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  if (loading) {
    return (
      <>
        <Breadcrumb title="Our Blog" />
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
            <p className="text-gray-600">Loading blogs...</p>
          </div>
        </section>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Breadcrumb title="Our Blog" />
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded">
              <p className="text-red-700 font-semibold">Error</p>
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchBlogs}
                className="mt-4 bg-[#009fe3] hover:bg-[#007cb5] text-white px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Breadcrumb title="Our Blog" />

      <section className="py-16 bg-gradient-to-b from-white to-gray-50 font-sans">
        <div className="max-w-7xl mx-auto px-4">
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search blogs..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent shadow-sm"
              />
              <button
                onClick={handleSearch}
                className="bg-[#009fe3] hover:bg-[#007cb5] text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 shadow-sm"
              >
                <FaSearch /> Search
              </button>
              {isSearching && (
                <button
                  onClick={clearSearch}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Category Filter - Below Search Bar - Rectangular Buttons */}
          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-3 max-w-5xl mx-auto">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => {
                    setActiveCategory(cat.value);
                    clearSearch();
                  }}
                  className={`px-6 py-2.5 font-semibold transition-all duration-200 ${
                    activeCategory === cat.value
                      ? "bg-[#009fe3] text-white shadow-md"
                      : "bg-white text-gray-700 border-2 border-gray-300 hover:border-[#009fe3] hover:text-[#009fe3] hover:bg-gray-50"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search Results */}
          {isSearching && (
            <div className="mb-14">
              <h3 className="bg-[#009fe3] text-white px-6 py-3 rounded-lg mb-8 text-2xl font-bold w-max mx-auto shadow-lg">
                Search Results ({searchResults.length})
              </h3>
              {searchResults.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl shadow-sm">
                  <p className="text-gray-500 text-lg">
                    No blogs found matching "{searchKeyword}"
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {currentSearchResults.map((post) => (
                      <BlogCard
                        key={post.id}
                        post={post}
                        formatDate={formatDate}
                        formatViewCount={formatViewCount}
                      />
                    ))}
                  </div>
                  {/* Pagination for Search Results */}
                  {totalSearchPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalSearchPages}
                      paginate={paginate}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* Highlighted Posts */}
          {!isSearching && highlightedPosts.length > 0 && (
            <div className="mb-14">
              <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                Featured Posts
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {highlightedPosts.map((post) => (
                  <HighlightedBlogCard
                    key={post.id}
                    post={post}
                    formatDate={formatDate}
                    formatViewCount={formatViewCount}
                  />
                ))}
              </div>
            </div>
          )}

          {/* All Blog Posts */}
          {!isSearching && allPosts.length > 0 && (
            <div>
              {highlightedPosts.length > 0 && (
                <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                  All Posts
                </h2>
              )}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                {currentPosts.map((post) => (
                  <BlogCard
                    key={post.id}
                    post={post}
                    formatDate={formatDate}
                    formatViewCount={formatViewCount}
                  />
                ))}
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  paginate={paginate}
                />
              )}
            </div>
          )}

          {/* No blogs found */}
          {!isSearching &&
            highlightedPosts.length === 0 &&
            allPosts.length === 0 && (
              <div className="text-center py-20 bg-white rounded-xl shadow-sm">
                <p className="text-gray-500 text-xl">
                  No blog posts available
                  {activeCategory !== "ALL" && " for this category"}.
                </p>
              </div>
            )}
        </div>
      </section>
    </>
  );
};

// Pagination Component
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  paginate: (pageNumber: number) => void;
}> = ({ currentPage, totalPages, paginate }) => {
  const pageNumbers = [];
  const maxVisiblePages = 5;

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="flex justify-center items-center gap-2 mt-12">
      {/* Previous Button */}
      <button
        onClick={() => paginate(currentPage - 1)}
        disabled={currentPage === 1}
        className={`flex items-center gap-2 px-4 py-2 font-semibold transition-all ${
          currentPage === 1
            ? "text-gray-400 cursor-not-allowed"
            : "text-[#009fe3] hover:bg-[#009fe3] hover:text-white border-2 border-[#009fe3]"
        }`}
      >
        <FaChevronLeft /> Previous
      </button>

      {/* First Page */}
      {startPage > 1 && (
        <>
          <button
            onClick={() => paginate(1)}
            className="px-4 py-2 font-semibold text-gray-700 hover:bg-[#009fe3] hover:text-white border-2 border-gray-300 hover:border-[#009fe3] transition-all"
          >
            1
          </button>
          {startPage > 2 && <span className="text-gray-500">...</span>}
        </>
      )}

      {/* Page Numbers */}
      {pageNumbers.map((number) => (
        <button
          key={number}
          onClick={() => paginate(number)}
          className={`px-4 py-2 font-semibold transition-all ${
            currentPage === number
              ? "bg-[#009fe3] text-white border-2 border-[#009fe3]"
              : "text-gray-700 hover:bg-[#009fe3] hover:text-white border-2 border-gray-300 hover:border-[#009fe3]"
          }`}
        >
          {number}
        </button>
      ))}

      {/* Last Page */}
      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && (
            <span className="text-gray-500">...</span>
          )}
          <button
            onClick={() => paginate(totalPages)}
            className="px-4 py-2 font-semibold text-gray-700 hover:bg-[#009fe3] hover:text-white border-2 border-gray-300 hover:border-[#009fe3] transition-all"
          >
            {totalPages}
          </button>
        </>
      )}

      {/* Next Button */}
      <button
        onClick={() => paginate(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`flex items-center gap-2 px-4 py-2 font-semibold transition-all ${
          currentPage === totalPages
            ? "text-gray-400 cursor-not-allowed"
            : "text-[#009fe3] hover:bg-[#009fe3] hover:text-white border-2 border-[#009fe3]"
        }`}
      >
        Next <FaChevronRight />
      </button>
    </div>
  );
};

// Blog Card Component (16:9 aspect ratio)
const BlogCard: React.FC<{
  post: BlogPost;
  formatDate: (date: string) => string;
  formatViewCount: (count: number) => string;
}> = ({ post, formatDate, formatViewCount }) => {
  const [hasLoved, setHasLoved] = React.useState(false);
  const [loveCount, setLoveCount] = React.useState(post.loveCount || 0);

  // Check if user has already loved this blog (from localStorage)
  React.useEffect(() => {
    const loved = localStorage.getItem(`blog_loved_${post.id}`) === "true";
    setHasLoved(loved);
  }, [post.id]);

  // Handle love click
  const handleLoveClick = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation to blog details
    e.stopPropagation();

    if (hasLoved) return;

    try {
      await fetch(`${API_BASE}/api/public/blogs/${post.id}/love`, {
        method: "GET",
        credentials: "include",
      });

      // Update local state
      setHasLoved(true);
      setLoveCount((prev) => prev + 1);

      // Save to localStorage
      localStorage.setItem(`blog_loved_${post.id}`, "true");
    } catch (err) {
      console.error("⚠️ Failed to increment love count:", err);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
      <Link to={`/blog/${post.id}`}>
        <img
          src={post.imageUrl || "/assets/img/blog/default-blog.jpg"}
          alt={post.title}
          className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </Link>
      <div className="p-5">
        {/* Category Badge */}
        <div className="mb-3">
          <span className="inline-block bg-[#009fe3] text-white px-3 py-1 rounded-full text-xs font-semibold">
            {post.categoryDisplay}
          </span>
        </div>

        <h5 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-[#009fe3] transition-colors duration-300 line-clamp-2">
          <Link to={`/blog/${post.id}`}>{post.title}</Link>
        </h5>
        <p className="text-gray-500 text-sm mb-2">
          {formatDate(post.publishDate)} &bull; {post.tags}
        </p>
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
          {post.excerpt}
        </p>

        {/* View Count, Love Count & Author */}
        <div className="flex items-center justify-between text-sm pt-3 border-t border-gray-100">
          <p className="text-gray-600 font-medium">
            <span className="text-[#009fe3]">{post.authorName}</span>
          </p>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-gray-500">
              <FaEye />
              <span>{formatViewCount(post.viewCount)}</span>
            </div>
            <button
              onClick={handleLoveClick}
              disabled={hasLoved}
              className={`flex items-center gap-1 transition-colors ${
                hasLoved
                  ? "text-red-600 cursor-not-allowed"
                  : "text-gray-400 hover:text-red-600 cursor-pointer"
              }`}
              title={
                hasLoved ? "You already loved this post" : "Love this post"
              }
            >
              <FaHeart />
              <span>{loveCount}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Highlighted Blog Card Component (16:9 aspect ratio)
const HighlightedBlogCard: React.FC<{
  post: BlogPost;
  formatDate: (date: string) => string;
  formatViewCount: (count: number) => string;
}> = ({ post, formatDate, formatViewCount }) => {
  const [hasLoved, setHasLoved] = React.useState(false);
  const [loveCount, setLoveCount] = React.useState(post.loveCount || 0);

  // Check if user has already loved this blog (from localStorage)
  React.useEffect(() => {
    const loved = localStorage.getItem(`blog_loved_${post.id}`) === "true";
    setHasLoved(loved);
  }, [post.id]);

  // Handle love click
  const handleLoveClick = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation to blog details
    e.stopPropagation();

    if (hasLoved) return;

    try {
      await fetch(`${API_BASE}/api/public/blogs/${post.id}/love`, {
        method: "GET",
        credentials: "include",
      });

      // Update local state
      setHasLoved(true);
      setLoveCount((prev) => prev + 1);

      // Save to localStorage
      localStorage.setItem(`blog_loved_${post.id}`, "true");
    } catch (err) {
      console.error("⚠️ Failed to increment love count:", err);
    }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-[#009fe3] shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group">
      <Link to={`/blog/${post.id}`}>
        <img
          src={post.imageUrl || "/assets/img/blog/default-blog.jpg"}
          alt={post.title}
          className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </Link>
      <div className="p-6">
        {/* Category Badge */}
        <div className="mb-3">
          <span className="inline-block bg-[#009fe3] text-white px-4 py-1.5 rounded-full text-sm font-semibold">
            {post.categoryDisplay}
          </span>
        </div>

        <h4 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-[#009fe3] transition-colors duration-300">
          <Link to={`/blog/${post.id}`}>{post.title}</Link>
        </h4>
        <div className="text-sm text-gray-500 mb-3">
          {formatDate(post.publishDate)} &bull; {post.tags}
        </div>
        <p className="text-gray-600 text-base mb-4 line-clamp-3">
          {post.excerpt}
        </p>

        {/* View Count, Love Count & Author */}
        <div className="flex items-center justify-between text-sm pt-4 border-t border-gray-200">
          <p className="text-gray-700 font-semibold">
            <span className="text-[#009fe3]">{post.authorName}</span>
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-gray-600">
              <FaEye />
              <span>{formatViewCount(post.viewCount)} views</span>
            </div>
            <button
              onClick={handleLoveClick}
              disabled={hasLoved}
              className={`flex items-center gap-1.5 transition-colors ${
                hasLoved
                  ? "text-red-600 cursor-not-allowed"
                  : "text-gray-400 hover:text-red-600 cursor-pointer"
              }`}
              title={
                hasLoved ? "You already loved this post" : "Love this post"
              }
            >
              <FaHeart />
              <span>{loveCount}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
